<template>
  <v-dialog :retain-focus="false" :value="sectionEditor.show" :width="1100" @input="handleClosePopup">
    <div style="background-color: #fff">
      <div class="header-modal">
        <v-card-title class="d-flex justify-space-between align-center" style="padding: 10px 0">
          <span style="color: #0081c7">{{ fields.title }}</span>
          <v-btn icon @click="handleClosePopup">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
      </div>
      <v-card-text>
        <div class="mb-4">
          <label class="label-input">Section Name</label>
          <v-text-field v-model="fields.title" hide-details />
        </div>

        <SectionLayout :showCustomWidth="false" :settings="fields.settings" :container-page="fields.containerPage" />

        <div class="mb-5">
          <label class="label-input">Title Section</label>
          <div class="group-item">
            <v-checkbox v-model="fields.cardTitle.show" label="Show title block" hide-details class="mt-0 mb-2" />
            <ckeditor
              v-if="fields.cardTitle.show"
              :editor="editor"
              v-model="fields.cardTitle.content"
              :config="editorConfig"
            />
          </div>
        </div>

        <div class="mb-5">
          <label class="label-input">Marquee Setting</label>
          <div class="group-item">
            <v-row dense>
              <v-col cols="4">
                <label>Speed (giây / vòng chạy)</label>
                <v-text-field
                  v-model.number="fields.speed"
                  type="number"
                  min="1"
                  hide-details
                  class="mt-0"
                  suffix="s"
                />
              </v-col>
              <v-col cols="4">
                <label>Item Width</label>
                <v-text-field
                  v-model.number="fields.itemWidth"
                  type="number"
                  min="1"
                  hide-details
                  class="mt-0"
                  suffix="px"
                />
              </v-col>
              <v-col cols="4">
                <label>Item Gap</label>
                <v-text-field
                  v-model.number="fields.itemGap"
                  type="number"
                  min="0"
                  hide-details
                  class="mt-0"
                  suffix="px"
                />
              </v-col>
            </v-row>
          </div>
        </div>

        <div class="mb-5">
          <div class="d-flex justify-space-between align-center mb-2">
            <label class="label-input mb-0">Items (max {{ MAX_ITEMS }})</label>
            <v-btn small color="primary" :disabled="fields.items.length >= MAX_ITEMS" @click="handleAddItem">
              <v-icon size="16">mdi-plus</v-icon>
              <span>Add Item</span>
            </v-btn>
          </div>
          <div class="group-item">
            <v-expansion-panels accordion>
              <v-expansion-panel v-for="(item, index) in fields.items" :key="`marquee-item-${index}`">
                <v-expansion-panel-header>
                  <div class="d-flex justify-space-between align-center" style="width: 100%">
                    <span>Item {{ index + 1 }}</span>
                    <v-btn icon :disabled="fields.items.length <= MIN_ITEMS" @click.stop="handleRemoveItem(index)">
                      <v-icon size="20" color="#ff6c6c">mdi-delete</v-icon>
                    </v-btn>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <UploadImage
                    :image="{ imageSrc: item.image }"
                    :height="`160`"
                    :typeUpload="'image'"
                    @callback="obj => (item.image = obj.imageSrc)"
                    @callbackDelete="item.image = ''"
                  />
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
        </div>
      </v-card-text>
      <div class="footer-modal">
        <v-card-actions style="padding: 10px 0">
          <v-spacer></v-spacer>
          <v-btn min-width="100" depressed @click="handleClosePopup">Cancel</v-btn>
          <v-btn min-width="100" color="primary" @click="handleSave">Save</v-btn>
        </v-card-actions>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import { cloneDeep } from 'lodash';
import Editor from 'ckeditor5-custom-build/build/ckeditor';
import { ckeditorConfigFullToolbar } from '@/helpers/editor';
import { updateHtmlFromData } from '../common/html/update';
import UploadImage from '../common/form/upload-file/upload-image.vue';
import SectionLayout from '../common/ui-modules/section-layout/editor.vue';

const MAX_ITEMS = 14;
const MIN_ITEMS = 3;

const buildItemDefault = () => ({
  image: '',
});

const getDefaultFields = () => ({
  title: 'Calyrafit Marquee Section',
  settings: {
    paddingTop: true,
    paddingBottom: true,
    backgroundColor: '#FFFFFF',
    textColor: '#000000',
    overlayColor: '#000000',
    overlayOpacity: 0,
    showOverlay: false,
    backgroundImage: '',
    useCustomWidth: false,
    customWidth: 1204,
  },
  containerPage: {
    enableStyle: false,
    background: '#FFFFFF',
  },
  cardTitle: {
    show: true,
    content:
      '<h2 style="text-align: center;">Over 565K+ Happy Customers</h2><p style="text-align: center;">Designed to shape, support, and move with your body-helping women feel more confident, comfortable, and empowered every day.</p>',
  },
  speed: 25,
  itemGap: 30,
  itemWidth: 270,
  items: [{ image: '' }, { image: '' }, { image: '' }, { image: '' }, { image: '' }, { image: '' }, { image: '' }],
});

export default {
  name: 'CalyrafitMarqueeSectionEditor',
  components: { UploadImage, SectionLayout },
  props: {
    sectionEditor: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      MAX_ITEMS,
      MIN_ITEMS,
      editor: Editor,
      editorConfig: ckeditorConfigFullToolbar,
      fields: getDefaultFields(),
    };
  },
  mounted() {
    const def = getDefaultFields();
    const extract = cloneDeep(this.sectionEditor.sectionExtract || {});

    this.fields = {
      ...def,
      ...extract,
      settings: { ...def.settings, ...(extract.settings || {}) },
      containerPage: { ...def.containerPage, ...(extract.containerPage || {}) },
      cardTitle: { ...def.cardTitle, ...(extract.cardTitle || {}) },
      items: Array.isArray(extract.items) && extract.items.length ? extract.items.slice(0, MAX_ITEMS) : def.items,
    };
  },
  methods: {
    handleClosePopup() {
      this.$emit('close');
    },
    handleSave() {
      const payload = {
        ...this.fields,
        items: (this.fields.items || []).slice(0, MAX_ITEMS),
      };
      const updateHtml = updateHtmlFromData.sectionCalyrafitMarquee(this.sectionEditor.section.html, payload);
      this.$emit('update:section', {
        ...this.sectionEditor.section,
        title: this.fields.title,
        html: updateHtml,
      });
      this.$emit('close');
    },
    handleAddItem() {
      if (this.fields.items.length >= MAX_ITEMS) return;
      this.fields.items.push(cloneDeep(buildItemDefault()));
    },
    handleRemoveItem(index) {
      if (this.fields.items.length <= MIN_ITEMS) return;
      this.fields.items.splice(index, 1);
    },
  },
};
</script>

<style lang="scss" scoped>
.group-item {
  border: 1px solid #0000000f;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 16px;
}
.label-input {
  font-size: 18px !important;
  font-weight: 500;
  color: #000;
  margin-bottom: 10px;
  display: block;
}
.header-modal {
  position: sticky;
  top: 0;
  left: 0;
  z-index: 999;
  background-color: #fff;
  padding: 0 16px;
  box-shadow: 0px 0px 12px #0000000f;
}
.footer-modal {
  position: sticky;
  padding: 0 16px;
  bottom: 0;
  left: 0;
  z-index: 999;
  background-color: #fff;
  box-shadow: 0px 0px 12px #0000000f;
}
</style>
